// @givmo/board — one seat on the board.
//
// ══ WHAT THIS PAGE IS FOR ══════════════════════════════════════════════════════════════════════
//
// The roster answers "who is on this board"; this answers "what do we have recorded about this
// seat". Every other surface in the module resolves TO a seat — a vote, a signature, an attendance
// row, a distribution — and until now a seat had no address of its own.
//
// ══ THE FOUR THINGS IT MUST NOT GET WRONG ══════════════════════════════════════════════════════
//
//   * A SEAT IS NOT AN ACCOUNT, BUT A MEMBER JOINS WITH ONE (CBG-SEC2, D-CBG-16/17). An officer
//     invites; the member joins the seat into their own Givmo account by an address that matches or a
//     code sent to the seat's address, and the page says which account joined — something that
//     HAPPENED, never something an officer sets. A seat that never joins still serves: the officer
//     records its signatures and replies on the member's behalf. Disconnecting a joined account is
//     its own recorded act, an Owner's, with a reason and two notices; changing the seat's email
//     keeps the account joined and notifies the old address.
//   * A REMOVED SEAT IS STILL A SEAT. Removal is a status flip that keeps the row and its history,
//     because a former director's past acts must keep resolving to a named seat. This page opens for
//     one exactly as it does for a serving one, and says plainly that they no longer serve — the
//     same honesty `seatPickerLabel` carries where a removed seat is offered beside current ones.
//   * THE TWO CLOCKS MEAN DIFFERENT THINGS AND NEITHER STANDS IN FOR THE OTHER. `served_from` /
//     `served_to` are the ORGANIZATION'S account of a term; `recorded_at` / `recorded_end_at` are the
//     module's account of when it was told. On a seat the organization entered as history they can be
//     years apart, and flattening them would present a backfill as a contemporaneous record.
//   * THE TWO FACTS AUTHORIZE NOTHING (CBG-YR3-ROLES layer 1, D-CBG-21). Whether the person is a
//     director decides votes, quorum and written consents; their titles are labels. The header names
//     both in the one helper's words ("Secretary and Treasurer · not a director"), and the first card
//     says each once, with what everyone on the roster gets whatever they are. Nothing here may read
//     as a permission: every seat reads the same records, and the board's meetings' packets (a
//     committee's go to its members, CBG-YR6-MEET).
//
// A SEAT FOR SOMEONE WHO ISN'T ON THE BOARD (CBG-YR6-OUTSIDE, frame o3) says so first, lists its
// committees in the director-and-titles card's place, and leaves out what is the board's alone: a
// director's term ("Their service") and the board's getting-started list. Its joining card and
// invitation are the committee's.
//
// The write affordances are the roster's, reached from here so an officer who opened a seat does not
// have to go back to act on it. Composition, disconnecting and the seat's history are Owner-only;
// inviting is Member+Owner — the split that keeps invitations from being a path into changing who is
// on the board.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '../../board-routing';
import { isGivmoError, type BoardMemberTermOut, type OrgBoardMemberOut } from '@givmo/api-client';
import { Badge, Button, Card, CardBody, Skeleton, testIdProp, toast } from '@givmo/design-system';
import { PageShell } from '../../components/page-shell';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { displayInstant, todayAsDateInput } from '../../lib/display';
import { InvitationDialog } from './invitation-dialog';
// CBG-UX9: the Joining card's line, which the invite step after "Add a member" says too.
import { inviteStepWords } from './invite-step-words';
import { ReleaseAccountDialog } from './release-account-dialog';
import { RemoveSeatDialog } from './remove-seat-dialog';
import { SeatFormDialog } from './seat-form-dialog';
import {
  connectedAccountIsAdministrator,
  connectedAccountName,
  invitationExpiresAt,
  inviteActionLabel,
  isCommitteeOnlySeat,
  isHistoricalSeat,
  seatCommittees,
  seatConnection,
  serviceLine,
  statusLabel,
  statusTone,
} from './roster-decode';
import {
  carriedOverEntries,
  extractRosterHistory,
  mergeHistory,
  movedHere,
  rosterHistoryLine,
  rosterHistoryWhen,
} from './roster-history';
import { DirectorAndTitlesCard } from './director-and-titles-card';
import { OutsideCommitteesCard } from './outside-committees-card';
import { extractOfficerDuties } from './officer-duties';
import { capacityLine, keptTogether } from './officer-roles';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { useBoardOperations, useBoardVocab } from '../../board-runtime';
import { extractParameters } from '../parameters/parameters-decode';
import { SeatTermsList } from './terms-card';
import { extractDirectorClasses, seatTerms, termRule } from './terms-decode';
import { TermDialog } from './term-dialog';
import { termsWords as tw } from './terms-words';
import { GettingStartedCard } from '../onboarding/getting-started-card';

export function MemberDetailPage() {
  const operations = useBoardOperations();
  const { orgNoun } = useBoardVocab();
  const { memberId } = useParams({ from: '/authenticated/roster/$memberId' });
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canManageRoster = hasBoardPermission(permissions, 'board_member:manage');
  // Inviting the seat's member is `board_member:grant_access` — its own string, not implied by roster
  // composition and not implying it.
  const canInvite = hasBoardPermission(permissions, 'board_member:grant_access');

  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  // CBG-YR3-TERMS: "Record a term" (`term: null`) or "Correct" on one term.
  const [termDialog, setTermDialog] = useState<{ term: BoardMemberTermOut | null } | null>(null);

  const seatKey = ['board-member', orgRef, memberId] as const;

  const seatQuery = useQuery({
    queryKey: seatKey,
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardMemberOut> => {
      const { data } = await operations.officerGetBoardMember({
        path: { org_ref: orgRef ?? '', member_id: memberId },
        throwOnError: true,
      });
      return data;
    },
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: seatKey });
    await queryClient.invalidateQueries({ queryKey: ['board-members', orgRef] });
    // An edit, a removal or a disconnection each adds a line to the seat's history.
    await queryClient.invalidateQueries({ queryKey: ['board-member-history', orgRef, memberId] });
  };

  // The seat's history is the Owner's read: its addresses are the member's personal data.
  const historyQuery = useQuery({
    queryKey: ['board-member-history', orgRef, memberId],
    enabled: isReady && orgRef !== null && canManageRoster,
    queryFn: async () => {
      const { data } = await operations.officerListBoardMemberHistory({
        path: { org_ref: orgRef ?? '', member_id: memberId },
        throwOnError: true,
      });
      return extractRosterHistory(data);
    },
  });

  // CBG-YR3-ROLES layer 3 (`l3-member-duties`): what each title the person holds does in Givmo —
  // shown once the board has adopted its duties (the starting point does nothing until then).
  const dutiesQuery = useQuery({
    ...operations.officerGetBoardOfficerDutiesOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null,
    select: extractOfficerDuties,
  });

  // CBG-YR3-TERMS: the board's recorded limit (the count line beside the terms) and its classes (the
  // term dialog's choices). Officers only, like the whole page.
  const parametersQuery = useQuery({
    ...operations.officerListBoardGovernanceParametersOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null,
    select: extractParameters,
  });
  const classesQuery = useQuery({
    ...operations.officerListBoardDirectorClassesOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null && canManageRoster,
    select: extractDirectorClasses,
  });

  const releaseAccount = useMutation({
    mutationFn: async (reason: string) => {
      await operations.officerReleaseBoardMemberAccount({
        path: { org_ref: orgRef ?? '', member_id: memberId },
        body: { reason },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setReleasing(false);
      setReleaseError(null);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['board-member-history', orgRef, memberId] });
      toast.success('Disconnected. We’ve emailed them to say so.');
    },
    onError: (caught) =>
      setReleaseError(
        isGivmoError(caught) ? caught.message : 'We couldn’t disconnect it. Please try again.',
      ),
  });

  const removeSeat = useMutation({
    mutationFn: async (reason: string) => {
      await operations.officerRemoveBoardMember({
        path: { org_ref: orgRef ?? '', member_id: memberId },
        body: { reason },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setRemoving(false);
      await refresh();
      toast.success('This seat is now recorded as former.');
    },
    onError: (caught) =>
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t update that seat. Please try again.',
      ),
  });

  const seat = seatQuery.data ?? null;

  if (seatQuery.isPending) {
    return (
      <PageShell title="A board member" testId="page-member-detail">
        <Skeleton className="h-90 w-full rounded-lg" testId="member-detail-loading" />
      </PageShell>
    );
  }

  if (seatQuery.isError || seat === null) {
    return (
      <PageShell title="A board member" testId="page-member-detail">
        <Card testId="member-detail-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We couldn’t open this seat.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void seatQuery.refetch()}
              testId="member-detail-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const history = seat.service_history ?? null;
  const serving = seat.status === 'active';
  const connection = seatConnection(seat);
  const accountName = connectedAccountName(seat);
  const alsoAdministrator = connectedAccountIsAdministrator(seat);
  const invitationUntil = invitationExpiresAt(seat);
  // What the roster recorded, and the titles the migration carried over (read off the seat itself).
  const historyEntries = mergeHistory(historyQuery.data ?? [], carriedOverEntries(seat));
  // Someone who isn't on the board (CBG-YR6-OUTSIDE), and the committees they serve on.
  const outside = isCommitteeOnlySeat(seat);
  const committeeNames = seatCommittees(seat).map((row) => row.name);

  return (
    <PageShell
      title={seat.display_name}
      description={
        // CBG-YR6-OUTSIDE (frame o3): someone who isn't on the board, said once; the card below
        // names the committees, with each one's role and dates.
        outside ? (
          co.notOnTheBoard
        ) : // CBG-YR6-MEET (not drawn; frame c8's line): the committees this seat serves on, named as
        // the registry records them, under the seat's facts.
        (seat.committees ?? []).length > 0 ? (
          <span className="flex flex-col gap-2">
            <span>{capacityLine(seat)}</span>
            <span data-testid="member-detail-committees">
              {/* Each committee kept whole: a phone breaks the line between them. */}
              {cm.yourCommittees(
                (seat.committees ?? []).map((row) =>
                  keptTogether(cm.committeeWithRole(row.name, row.role === 'chair')),
                ),
              )}
            </span>
          </span>
        ) : (
          capacityLine(seat)
        )
      }
      actions={
        <Link
          to="/roster"
          // Exact: this page sits beneath that address, and it is not the current page.
          exact
          className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          data-testid="member-detail-back"
        >
          All board members
        </Link>
      }
      testId="page-member-detail"
    >
      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={statusTone(seat.status)}>{statusLabel(seat.status)}</Badge>
        {isHistoricalSeat(seat) ? <Badge tone="neutral">From your records</Badge> : null}
      </div>

      {!serving ? (
        // Said in words, not left to a badge. Somebody arriving here from a vote or a signature is
        // asking why this name is on a record, and "no longer serving" is the answer — not an error.
        <p
          className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
          data-testid="member-detail-former"
        >
          {outside
            ? co.formerLine
            : 'This seat no longer serves on the board. Everything it did while it did stays on the record — a vote, a signature or an attendance entry recorded then is still that seat’s.'}
        </p>
      ) : null}

      {/* ── Director and titles (CBG-YR3-ROLES layer 1); for someone who isn't on the board, their
          committees in its place (CBG-YR6-OUTSIDE) ────────────────────────────────────────────── */}
      {outside ? (
        <OutsideCommitteesCard seat={seat} />
      ) : (
        <DirectorAndTitlesCard seat={seat} duties={dutiesQuery.data ?? null} />
      )}

      {/* ── Who they are ──────────────────────────────────────────────────────────────────────── */}
      <Card testId="member-detail-identity">
        <CardBody className="flex flex-col gap-8">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            Where we write to them
          </h2>
          <span
            className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
            data-testid="member-detail-email"
          >
            {seat.email_of_record}
          </span>
          <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            Invitations and papers go here. Changing it doesn’t disconnect someone who has joined;
            we’ll email the old address to say it changed.
          </span>
        </CardBody>
      </Card>

      {/* ── The seat's terms, and the two clocks (CBG-YR3-TERMS, frame t1): a director's, never a
          committee-only seat's (CBG-YR6-OUTSIDE: its committees carry its dates) ───────────────── */}
      {outside ? null : (
        <Card testId="member-detail-service">
          <CardBody className="flex flex-col gap-12">
            <div className="flex flex-wrap items-start justify-between gap-12">
              <div className="flex min-w-0 flex-col gap-2">
                <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                  Their service
                </h2>
                {/* THE MODULE's own clock, kept separate and said as the SEAT's own entry, beside the
                  card's heading and apart from the terms and their count: on a backfilled seat these
                  are years apart from the dates below, and neither ever stands in for the other. */}
                {history?.recorded_at || history?.recorded_end_at ? (
                  <span
                    className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                    data-testid="member-detail-recorded"
                  >
                    {history.recorded_at
                      ? `Entered here ${displayInstant(history.recorded_at)}`
                      : null}
                    {history.recorded_at && history.recorded_end_at ? ' · ' : null}
                    {history.recorded_end_at
                      ? `service ended here ${displayInstant(history.recorded_end_at)}`
                      : null}
                  </span>
                ) : null}
                {seat.removed_at ? (
                  <span
                    className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                    data-testid="member-detail-removed-at"
                  >
                    Recorded as former {displayInstant(seat.removed_at)}
                  </span>
                ) : null}
              </div>
              {canManageRoster && serving ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => setTermDialog({ term: null })}
                  testId="member-terms-record"
                >
                  {tw.recordTerm}
                </Button>
              ) : null}
            </div>

            {/* The seat's terms, current first: the ORGANIZATION's account of each. With none recorded,
              the approved words, never a placeholder that suggests a value is missing. */}
            {seatTerms(seat).length > 0 ? (
              <SeatTermsList
                seat={seat}
                limit={termRule(parametersQuery.data ?? [], 'director_term_limit')}
                today={todayAsDateInput()}
                onCorrect={
                  canManageRoster && serving ? (term) => setTermDialog({ term }) : undefined
                }
              />
            ) : (
              <span
                className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
                data-testid="member-detail-service-line"
              >
                {serviceLine(seat) ?? 'No dates recorded for this seat.'}
              </span>
            )}
          </CardBody>
        </Card>
      )}

      {termDialog !== null && orgRef !== null ? (
        <TermDialog
          orgRef={orgRef}
          seat={seat}
          term={termDialog.term}
          classes={classesQuery.data ?? []}
          limitRecorded={termRule(parametersQuery.data ?? [], 'director_term_limit') !== null}
          onClose={() => setTermDialog(null)}
          onDone={() => {
            setTermDialog(null);
            void refresh();
          }}
        />
      ) : null}

      {/* CBG-YR4-START (frame g1): the member's getting-started list, while they serve — the board's
          own list, never a committee-only seat's (CBG-YR6-OUTSIDE). */}
      {serving && !outside ? <GettingStartedCard seat={seat} /> : null}

      {/* ── Joining the board ──────────────────────────────────────────────────────────────────── */}
      {/* Only while the seat serves: someone no longer on the board can't be invited or join, so the
          card would say something untrue about them ("Not invited yet."). Their history keeps how
          they joined and left. */}
      {serving ? (
        <Card testId="member-detail-account">
          <CardBody className="flex flex-col gap-8">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {outside ? co.joiningTitle(committeeNames) : 'Joining the board'}
            </h2>

            <span
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="member-detail-account-state"
            >
              {connection === 'joined'
                ? `${
                    // Frame b8, after the director moved their seat; the seat's link is the move's
                    // instant. Read off the history, which Owners read.
                    movedHere(historyQuery.data ?? [])
                      ? `Moved to ${accountName === null ? 'another' : `${accountName}’s`}`
                      : `Joined with ${accountName === null ? 'a' : `${accountName}’s`}`
                  } Givmo account${
                    seat.linked_at ? ` on ${displayInstant(seat.linked_at)}` : ''
                  }.${alsoAdministrator === true ? ` That account is also an administrator of this ${orgNoun}.` : ''}`
                : connection === 'invited'
                  ? `Invited.${invitationUntil !== null ? ` The invitation works until ${displayInstant(invitationUntil)}.` : ''}`
                  : 'Not invited yet.'}
            </span>
            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {/* A fact, not a control: a member joins from the invitation, into their own account. */}
              {connection === 'joined'
                ? 'They read and sign with that account. Signing asks them to confirm it’s them.'
                : inviteStepWords.lead}
            </span>
          </CardBody>
        </Card>
      ) : null}

      {/* ── What can be done ──────────────────────────────────────────────────────────────────── */}
      {/* Only the acts the person holds — an invitation is `board_member:grant_access`; editing,
          disconnecting or ending the seat is `board_member:manage` — and no card at all when that is
          none. */}
      {serving && (canInvite || canManageRoster) ? (
        <Card testId="member-detail-actions">
          <CardBody className="flex flex-col items-start gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Looking after this seat
            </h2>
            <div className="flex flex-wrap gap-10">
              {/* A committee-only seat whose every membership has ended opens nothing: no invitation. */}
              {canInvite && connection !== 'joined' && !(outside && committeeNames.length === 0) ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  onClick={() => setInviting(true)}
                  testId="member-detail-invite"
                >
                  {outside && connection !== 'invited'
                    ? co.inviteTo(committeeNames)
                    : inviteActionLabel(connection)}
                </Button>
              ) : null}
              {canManageRoster && connection === 'joined' ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  onClick={() => {
                    setReleaseError(null);
                    setReleasing(true);
                  }}
                  testId="member-detail-release"
                >
                  Disconnect account
                </Button>
              ) : null}
              {canManageRoster ? (
                <>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    onClick={() => setEditing(true)}
                    testId="member-detail-edit"
                  >
                    Edit this seat
                  </Button>
                  <Button
                    appearance="saas"
                    tone="ghost"
                    onClick={() => setRemoving(true)}
                    testId="member-detail-remove"
                  >
                    Record as former
                  </Button>
                </>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {editing && orgRef !== null ? (
        <SeatFormDialog
          orgRef={orgRef}
          mode="edit"
          seat={seat}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      ) : null}

      {/* ── History (Owner) ─────────────────────────────────────────────────────────────────── */}
      {canManageRoster ? (
        <Card testId="member-detail-history">
          <CardBody className="flex flex-col gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              History
            </h2>
            {historyQuery.isPending ? (
              <Skeleton className="h-48 w-full rounded-lg" testId="member-detail-history-loading" />
            ) : historyQuery.isError ? (
              <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                We couldn’t load this seat’s history.
              </span>
            ) : historyEntries.length === 0 ? (
              <span
                className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
                {...testIdProp('member-detail-history-empty')}
              >
                Nothing recorded yet.
              </span>
            ) : (
              <ul className="flex flex-col gap-8">
                {historyEntries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-col gap-2"
                    data-testid={`member-detail-history-${entry.id}`}
                  >
                    <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      {rosterHistoryLine(entry)}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {rosterHistoryWhen(entry)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      ) : null}

      {inviting && orgRef !== null ? (
        <InvitationDialog
          orgRef={orgRef}
          seat={seat}
          committees={outside ? committeeNames : null}
          onClose={() => {
            setInviting(false);
            void refresh();
            void queryClient.invalidateQueries({
              queryKey: ['board-member-history', orgRef, memberId],
            });
          }}
        />
      ) : null}

      {releasing ? (
        <ReleaseAccountDialog
          seat={seat}
          pending={releaseAccount.isPending}
          error={releaseError}
          onCancel={() => setReleasing(false)}
          onConfirm={(reason) => releaseAccount.mutate(reason)}
        />
      ) : null}

      {removing ? (
        <RemoveSeatDialog
          seat={seat}
          pending={removeSeat.isPending}
          onCancel={() => setRemoving(false)}
          onConfirm={(reason) => removeSeat.mutate(reason)}
        />
      ) : null}
    </PageShell>
  );
}
